import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import './Login.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_HOSTED_DOMAIN = process.env.REACT_APP_GOOGLE_HOSTED_DOMAIN || 'sheshalogistics.com';

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const completeLogin = (data) => {
    const userData = {
      user_id: data.user_id,
      username: data.username,
      role: data.role,
      full_name: data.full_name,
    };
    onLogin(data.access_token, userData);
    navigate('/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || data.msg || `Login failed (${response.status})`);
        setLoading(false);
        return;
      }

      if (data && data.access_token) {
        completeLogin(data);
      } else {
        setError('Invalid response from server: missing access_token');
        setLoading(false);
      }
    } catch (err) {
      setError('Error logging in: ' + err.message);
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credential: credentialResponse.credential,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Google sign-in failed');
        setLoading(false);
        return;
      }

      if (data && data.access_token) {
        completeLogin(data);
      } else {
        setError('Invalid response from server: missing access_token');
        setLoading(false);
      }
    } catch (err) {
      setError('Google sign-in error: ' + err.message);
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>Shesha Logistics</h1>
          <p>Inventory Management System</p>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {GOOGLE_CLIENT_ID && (
          <div className="google-login-section">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => {
                setError('Google sign-in was cancelled or failed');
                setLoading(false);
              }}
              hosted_domain={GOOGLE_HOSTED_DOMAIN}
              useOneTap={false}
              theme="outline"
              size="large"
              text="signin_with"
              shape="rectangular"
              width="320"
            />
            <div className="login-divider">
              <span>or use username</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              required={!GOOGLE_CLIENT_ID}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required={!GOOGLE_CLIENT_ID}
              disabled={loading}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        {!GOOGLE_CLIENT_ID && (
          <div className="login-footer">
            <p>Demo Credentials:</p>
            <small>Username: admin</small>
            <br />
            <small>Password: admin_password</small>
          </div>
        )}
      </div>
    </div>
  );
}
